// // @import
// import { autoUpdate, computePosition, flip, type Placement } from "@floating-ui/dom";
// import { viewport } from "@src/js/lib/viewport";
import { ensureHTMLElement, ensureHTMLNodeList } from "@src/js/lib/ensure";
import { viewport } from "@src/js/lib/viewport";
import Alpine from "alpinejs";
import type { EquipmentIndex, Scene, ScenesStore } from "./types";

const createStore = (): Scene => {
  const store: Scene = {
    equipments: [],
    activeEquipment: "-1:-1",
    setActiveEquipment: function (id: EquipmentIndex) {
      this.activeEquipment = id;
    },
    unsetActiveEquipment: function () {
      this.activeEquipment = "-1:-1";
    },
    nextEquipment: function () {
      const currentIndex = Number(this.activeEquipment.split(":")[0]);
      const nextIndex = (currentIndex + 1) % this.equipments.length;
      this.activeEquipment = this.equipments[nextIndex];
    },
    previousEquipment: function () {
      const currentIndex = Number(this.activeEquipment.split(":")[0]);
      const previousIndex = (currentIndex - 1 + this.equipments.length) % this.equipments.length;
      this.activeEquipment = this.equipments[previousIndex];
    },
  };
  return store;
};

function getDefaultActiveEquipment(equipmentIds: EquipmentIndex[]) {
  return equipmentIds.length > 0 ? equipmentIds[Math.round(equipmentIds.length / 2)] : "-1:-1";
}

Alpine.directive("scene", (element, { modifiers }, { cleanup, effect }) => {
  //   const panelPlacementDefault = "bottom";
  const dom = {
    inner: ensureHTMLElement(element.querySelector(".c-scene__inner")),
    dots: ensureHTMLNodeList(element.querySelectorAll(".c-equipment-dot")),
    btnPrev: ensureHTMLElement(element.querySelector(".c-scene__btn-prev")),
    btnNext: ensureHTMLElement(element.querySelector(".c-scene__btn-next")),
  };

  const equipmentsIds = Array.from(dom.dots).map(
    (dot) => dot.getAttribute("data-equipment")!,
  ) as EquipmentIndex[];

  const scene = store.createScene(modifiers[0] || "default", equipmentsIds);

  function handleTabChange(event: Event) {
    const customEvent = event as CustomEvent;
    if (customEvent.detail.tabId === modifiers[0]) {
      // Reset active equipment when the scene's tab is selected
      scene.setActiveEquipment(getDefaultActiveEquipment(equipmentsIds));
    } else {
      scene.unsetActiveEquipment();
    }
  }

  function handleNextClick(event: MouseEvent) {
    event.stopPropagation();
    scene.nextEquipment();
  }

  function handlePreviousClick(event: MouseEvent) {
    event.stopPropagation();
    scene.previousEquipment();
  }

  effect(() => {
    if (viewport.width) {
      const activeEquipment = scene.activeEquipment;
      const activeDotElement = element.querySelector(
        `.c-equipment-dot[data-equipment="${activeEquipment}"]`,
      ) as HTMLElement | null;
      const xActive = activeDotElement?.getAttribute("data-x") || "50";
      // Calculate the target translateX to center the active dot
      const innerW = dom.inner.getBoundingClientRect().width;
      const vw = window.innerWidth;
      const overflow = Math.max(0, innerW - vw);
      const centeringOffset = -overflow / 2;
      const focusPct = parseFloat(xActive) || 50;
      const targetPx = centeringOffset + innerW * (0.5 - focusPct / 100);
      const clamped = Math.min(Math.max(targetPx, -overflow), 0);
      element.style.setProperty("--translate-x", `${clamped}px`);
    }
  });

  // Event listeners
  dom.btnPrev.addEventListener("click", handlePreviousClick);
  dom.btnNext.addEventListener("click", handleNextClick);
  window.addEventListener("tabChange", handleTabChange);

  // Cleanup
  cleanup(() => {
    window.removeEventListener("tabChange", handleTabChange);
    dom.btnPrev.removeEventListener("click", handlePreviousClick);
    dom.btnNext.removeEventListener("click", handleNextClick);
  });
});

// Scene state
Alpine.store("scenes", {
  scenes: {} as Record<string, Scene>,
  getScene(id: string) {
    return this.scenes[id];
  },
  createScene(id: string, equipmentIds: EquipmentIndex[] = []) {
    if (!this.scenes[id]) {
      this.scenes[id] = createStore();
    }
    this.scenes[id].equipments = equipmentIds;
    this.scenes[id].activeEquipment = getDefaultActiveEquipment(equipmentIds);
    return this.scenes[id];
  },
} as ScenesStore);

export const store = Alpine.store("scenes") as ScenesStore;
